import React, { useEffect, useState } from 'react';
import { Button, Card, Form } from 'react-bootstrap';
import AsyncSelect from 'react-select/async';
import { unwrapResult } from '@reduxjs/toolkit';
import { MultiValue } from 'react-select';
import { useTranslation } from 'react-i18next';
import { useAppDispatch } from '../../app/hooks';
import {
  AutoSuggestActionMeta,
  AutoSuggestionItem,
  AutoSuggestOption,
  fetchAutoSuggestions,
} from './autocompleteSlice';
import { LOCALES } from '../../common/constants';
import { Loader } from '../../common/components';

interface AccommodationAutoCompleteSearchProps {
  onSelect: (selectedItems: MultiValue<AutoSuggestOption>) => void;
  selectedOptions: MultiValue<AutoSuggestOption>;
  locale?: string;
  placeholder?: string;
  isLoading: boolean;
  searchHandler: (event: React.FormEvent<HTMLFormElement>) => void;
  resetHandler?: () => void;
  showResetButton?: boolean;
  handleSearchTermChange: (inputValue: string) => void;
  searchTerm: string;
  children?: React.ReactNode;
}

const AccommodationAutoCompleteSearchBar: React.FC<
  AccommodationAutoCompleteSearchProps
> = ({
  onSelect,
  selectedOptions,
  locale = LOCALES.Fi,
  placeholder,
  isLoading,
  searchHandler,
  resetHandler,
  showResetButton = false,
  handleSearchTermChange,
  searchTerm,
  children,
}) => {
  const { t } = useTranslation([
    'common',
    'accommodationAutoCompleteSearchBar',
  ]);
  const dispatch = useAppDispatch();
  const [optionSelected, setOptionSelected] = useState<boolean>(false);

  useEffect(() => {
    if (optionSelected) {
      handleSearchTermChange('');
    }
  }, [optionSelected, handleSearchTermChange]);

  const loadOptions = async (inputValue: string) => {
    if (!inputValue) return [];
    const resultAction = await dispatch(
      fetchAutoSuggestions({ prefix: inputValue, locale }),
    );
    const fetchedSuggestions = unwrapResult(
      resultAction,
    ) as AutoSuggestionItem[];
    return fetchedSuggestions.map((suggestion: AutoSuggestionItem) => ({
      value: suggestion.id,
      label: suggestion.name,
      type: suggestion.type,
      isParent: suggestion.isParent,
      description: suggestion.description,
    }));
  };

  const handleChange = (newSelectedOptions: MultiValue<AutoSuggestOption>) => {
    onSelect(newSelectedOptions);
  };

  const handleInputChange = (
    newValue: string,
    actionMeta: AutoSuggestActionMeta,
  ) => {
    if (actionMeta.action === 'set-value') {
      setOptionSelected(true);
    }
    if (actionMeta.action === 'input-change' && newValue !== '') {
      setOptionSelected(false);
    }

    if (
      actionMeta.action === 'menu-close' ||
      actionMeta.action === 'input-blur'
    ) {
      handleSearchTermChange(actionMeta.prevInputValue || newValue);
    } else {
      handleSearchTermChange(newValue);
    }
  };

  const handleFormSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    searchHandler(event);
  };

  return (
    <Card className="bg-white border shadow-none p-0 mb-40">
      <Loader IsLoading={isLoading} />
      <Form
        id="searchBarForm"
        className={isLoading ? 'd-none' : ''}
        onSubmit={e => handleFormSubmit(e)}
      >
        <div className="d-flex p-24">
          <div className="form-control-dark flex-grow-1 me-24">
            <AsyncSelect
              styles={{
                control: baseStyles => ({
                  ...baseStyles,
                  borderColor: '#ced4da',
                  borderRadius: 6,
                }),
                dropdownIndicator: baseStyles => ({
                  ...baseStyles,
                  visibility: 'hidden',
                }),
                indicatorSeparator: baseStyles => ({
                  ...baseStyles,
                  visibility: 'hidden',
                }),
              }}
              classNames={{
                control: state => 'form-control form-control-search ps-48 pe-8',
              }}
              noOptionsMessage={() =>
                t('accommodationAutoCompleteSearchBar:noResults')
              }
              inputId="searchTerm"
              name="searchTerm"
              placeholder={
                placeholder ||
                t('accommodationAutoCompleteSearchBar:placeHolderText')
              }
              isClearable
              isSearchable
              loadOptions={loadOptions}
              cacheOptions
              onChange={handleChange}
              onInputChange={handleInputChange}
              inputValue={searchTerm}
              isMulti
              value={selectedOptions}
            />
          </div>
          <Button type="submit" variant="primary">
            {t('common:buttons.search')}
          </Button>
          {showResetButton && resetHandler && (
            <Button
              type="button"
              variant="secondary"
              onClick={resetHandler}
              className="ms-24"
            >
              {t('common:buttons.emptySearchFilters')}
            </Button>
          )}
        </div>
      </Form>
      {children}
    </Card>
  );
};

export default AccommodationAutoCompleteSearchBar;
